/**
 * Bearri links — thin Clerk-authed wrappers over `/api/links`.
 *
 * A Bearri link forwards to the brand's exact URL with a plain redirect:
 * nothing is added, removed or rewritten, and no cookie is set. Each click is
 * logged anonymously (device class, the referring site's host, the time, and
 * a country only when the host provides one). The stats are clicks on the
 * link — not buyers, not views — and are never added to any other number.
 *
 * The server refuses a link it cannot forward honestly (Amazon, Bearri's own
 * hosts, a host that isn't the deal's brand) with a plain sentence in
 * `detail`; the UI shows that sentence as-is.
 */
import apiConfig from '../config/apiConfig';
import { errorFromResponse } from './apiError';

export interface LinkStats {
  total_clicks: number;
  clicks_7d: number;
  clicks_30d: number;
  last_click_at: string | null;
  by_device: { mobile: number; desktop: number; tablet: number; other: number };
  top_referrers: Array<{ host: string; clicks: number }>;
  top_countries: Array<{ country: string; clicks: number }>;
}

export interface Link {
  id: string;
  slug: string;
  /** The Bearri link people click. */
  url: string;
  /** Where it forwards, byte for byte. */
  destination_url: string;
  destination_host: string;
  label: string | null;
  pitch_id: string | null;
  brand_name: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  stats: LinkStats;
}

export interface LinkCreateInput {
  destination_url: string;
  label?: string;
  pitch_id?: string;
  /** The creator's own confirmation that their deal allows a forwarding link
   *  (or the brand said yes on the deal link). The server refuses without it. */
  brand_allows_redirect: boolean;
}

export interface LinkPatchInput {
  destination_url?: string;
  label?: string;
  active?: boolean;
}

async function call<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiConfig.baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) throw await errorFromResponse(res);
  return res.json() as Promise<T>;
}

export const listLinks = (token: string) => call<{ links: Link[] }>(token, '/api/links');

export const createLink = (token: string, input: LinkCreateInput) =>
  call<Link>(token, '/api/links', { method: 'POST', body: JSON.stringify(input) });

export const updateLink = (token: string, id: string, patch: LinkPatchInput) =>
  call<Link>(token, `/api/links/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });

/** Removes the link from the list and stops it forwarding. Clicks it already
 *  had stay in the deal's results. */
export const retireLink = (token: string, id: string) =>
  call<{ ok: true }>(token, `/api/links/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Client-side check before the server's: the server is still the judge. */
export function destinationProblem(raw: string): string | null {
  const value = raw.trim();
  if (!value) return 'Paste the brand’s link.';
  if (!/^https?:\/\//i.test(value)) return 'Paste the full link, starting with https://';
  if (/\s/.test(value)) return 'That link has a space in it — paste it again exactly as the brand sent it.';
  if (value.length > 2048) return 'That link is too long to forward (2,048 characters at most).';
  return null;
}
